import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { container } from "tsyringe";
import { match } from "ts-pattern";
import DefaultAvatar from "assets/images/default-avatar.svg";
import { EGender } from "common/enums/Gender/EGenter";
import { ELang } from "common/enums/User/ELang";
import { OmitRecursively, ValidationError } from "common/resources/Resource";
import UserAccountInfoUpdateRequestResource from "common/resources/User/UserAccountInfoUpdateRequestResource";
import Button, { EButtonColor, EButtonSize, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import Form from "components/materials/Form";
import Input from "components/materials/Form/InputElement";
import I18n from "components/materials/I18n";
import Select from "components/materials/Form/SelectElement";
import PageTemplate from "components/PageTemplate";
import ModuleConfig from "configs/ModuleConfig";
import { UserContext } from "contexts/User";
import UserService from "services/UserService";
import classes from "./classes.module.scss";

const userService = container.resolve(UserService);
const modules = container.resolve(ModuleConfig).get().modules;

export default function Profile() {
	const navigate = useNavigate();
	const { user, updateUser } = useContext(UserContext);
	const { profilePicture, profilePictureFile, onProfilePictureChange } = useProfilePicture();
	const { profilePictureInputRef, onProfilePictureClick } = useProfilePictureInput();
	const [errors, setErrors] = useState<ValidationError[]>([]);

	const onSubmit = useCallback(
		(formData: { [key: string]: unknown }) => {
			setErrors([]);

			const parseLang = (lang: string | null): ELang | null => {
				return match(lang)
					.with("en", () => ELang.EN)
					.with("fr", () => ELang.FR)
					.otherwise(() => null);
			};

			const userUpdatedInfo: OmitRecursively<UserAccountInfoUpdateRequestResource, "validateOrReject"> = {
				address: formData["address"] as string,
				city: formData["city"] as string,
				firstName: formData["firstName"] as string,
				lastName: formData["lastName"] as string,
				phoneNumber: formData["phoneNumber"] as string,
				zipCode: formData["zipCode"] as string,
				gender: formData["gender"] as EGender,
				birthDate: new Date(formData["birthDate"] as string),
				lang: parseLang(formData["lang"] as string) ?? ELang.FR,
			};

			if (formData["password"] !== "") {
				userUpdatedInfo.password = formData["password"] as string;
				userUpdatedInfo.confirmPassword = formData["password"] as string;
			}

			const userRequest = UserAccountInfoUpdateRequestResource.hydrate<UserAccountInfoUpdateRequestResource>(userUpdatedInfo);
			userRequest
				.validateOrReject()
				.then(() => {
					userService
						.updateAccount(userRequest, profilePictureFile)
						.then((updatedUser) => {
							updateUser(updatedUser);
							alert(I18n.trslt(I18n.asset.pages.profile.update_success));
							navigate(modules.pages.Home.props.path);
							window.location.reload();
						})
						.catch((error) => {
							console.error({ error });
							if (error instanceof Array) {
								setErrors(error);
							}
						});
				})
				.catch((error) => {
					console.error({ error });
					if (error instanceof Array) {
						setErrors(error);
					}
				});
		},
		[updateUser, navigate, profilePictureFile],
	);

	return (
		<PageTemplate tabTitle={I18n.trslt(I18n.asset.pages.profile.page_title)} mainSectionClassName={classes["root"]}>
			<div className={classes["profile-container"]}>
				<div className={classes["header"]} onClick={onProfilePictureClick}>
					<div className={classes["avatar"]}>
						<img src={profilePicture} alt="avatar" />
					</div>
					<input type="file" accept="image/*" ref={profilePictureInputRef} className={classes["input-file"]} onChange={onProfilePictureChange} />
					<Typography type="p" size="small" weight="medium">
						{I18n.trslt(I18n.asset.pages.profile.change_avatar)}
					</Typography>
				</div>

				<Form onSubmit={onSubmit} errors={errors} className={classes["form-container"]}>
					<div className={classes["all-inputs"]}>
						<div className={classes["inputs-container"]}>
							<Input
								name="firstName"
								label={I18n.trslt(I18n.asset.pages.profile.inputs.firstname.label)}
								placeholder={I18n.trslt(I18n.asset.pages.profile.inputs.firstname.placeholder)}
								className={classes["input"]}
								defaultValue={user?.firstName}
								inputClassName={classes["input-content"]}
							/>
							<Input
								name="lastName"
								label={I18n.trslt(I18n.asset.pages.profile.inputs.lastname.label)}
								placeholder={I18n.trslt(I18n.asset.pages.profile.inputs.lastname.placeholder)}
								className={classes["input"]}
								inputClassName={classes["input-content"]}
								defaultValue={user?.lastName}
							/>
						</div>

						<div className={classes["inputs-container"]}>
							<Select
								name="gender"
								label={I18n.trslt(I18n.asset.pages.profile.inputs.sex.label)}
								options={I18n.asset.pages.profile.inputs.sex.options}
								className={classes["input"]}
								defaultValue={user?.gender}
							/>
							<Input
								name="birthDate"
								label={I18n.trslt(I18n.asset.pages.profile.inputs.birthdate.label)}
								placeholder={I18n.trslt(I18n.asset.pages.profile.inputs.birthdate.placeholder)}
								className={classes["input"]}
								inputClassName={classes["input-content"]}
								type="date"
								max={new Date().toISOString().split("T")[0]}
								defaultValue={user?.birthDate ? new Date(user?.birthDate).toISOString().split("T")[0] : ""}
							/>
						</div>

						<div className={classes["inputs-container"]}>
							<Input
								name="phoneNumber"
								label={I18n.trslt(I18n.asset.pages.profile.inputs.phone.label)}
								placeholder={I18n.trslt(I18n.asset.pages.profile.inputs.phone.placeholder)}
								className={classes["input"]}
								inputClassName={classes["input-content"]}
								defaultValue={user?.phoneNumber}
							/>
							<Input
								name="city"
								label={I18n.trslt(I18n.asset.pages.profile.inputs.city.label)}
								placeholder={I18n.trslt(I18n.asset.pages.profile.inputs.city.placeholder)}
								className={classes["input"]}
								inputClassName={classes["input-content"]}
								defaultValue={user?.city}
							/>
						</div>

						<div className={classes["inputs-container"]}>
							<Input
								name="address"
								label={I18n.trslt(I18n.asset.pages.profile.inputs.address.label)}
								placeholder={I18n.trslt(I18n.asset.pages.profile.inputs.address.placeholder)}
								className={classes["input"]}
								inputClassName={classes["input-content"]}
								defaultValue={user?.address}
							/>
							<Input
								name="zipCode"
								label={I18n.trslt(I18n.asset.pages.profile.inputs.zipCode.label)}
								placeholder={I18n.trslt(I18n.asset.pages.profile.inputs.zipCode.placeholder)}
								className={classes["input"]}
								inputClassName={classes["input-content"]}
								defaultValue={user?.zipCode}
							/>
						</div>

						<Input
							name="password"
							label={I18n.trslt(I18n.asset.pages.profile.inputs.password.label)}
							placeholder={I18n.trslt(I18n.asset.pages.profile.inputs.password.placeholder)}
							type="password"
							additionalInfo={I18n.trslt(I18n.asset.pages.profile.inputs.password.additional_info)}
							className={classes["input"]}
							inputClassName={classes["input-content"]}
						/>

						<Select
							name="lang"
							label={I18n.trslt(I18n.asset.pages.profile.inputs.lang.label)}
							options={I18n.asset.pages.profile.inputs.lang.options}
							className={classes["input"]}
							defaultValue={user?.lang?.toLocaleLowerCase() ?? I18n.getLang()}
						/>
					</div>

					<div className={classes["buttons-container"]}>
						<Link to={modules.pages.Home.props.path}>
							<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} size={EButtonSize.LARGE}>
								{I18n.trslt(I18n.asset.pages.profile.cancel_button)}
							</Button>
						</Link>

						<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} size={EButtonSize.LARGE} type="submit">
							{I18n.trslt(I18n.asset.pages.profile.submit_button)}
						</Button>
					</div>
				</Form>
			</div>
		</PageTemplate>
	);
}

const useProfilePicture = () => {
	const { user } = useContext(UserContext);
	const [profilePictureFile, setProfilePictureFile] = useState<File | undefined>(undefined);
	const [profilePicture, setProfilePicture] = useState<string>(user?.avatar?.url ?? DefaultAvatar);

	const onProfilePictureChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			setProfilePictureFile(file);
			const reader = new FileReader();
			reader.onload = (e) => {
				setProfilePicture(e.target?.result as string);
			};
			reader.readAsDataURL(file);
		}
	}, []);

	useEffect(() => {
		if (!user) return;
		userService.getProfilePictureUrl(user.id).then((url) => setProfilePicture(url ?? DefaultAvatar));
	}, [user]);

	return {
		profilePicture,
		profilePictureFile,
		onProfilePictureChange,
	};
};

const useProfilePictureInput = () => {
	const profilePictureInputRef = useRef<HTMLInputElement>(null);

	const onProfilePictureClick = useCallback(() => {
		if (profilePictureInputRef.current) {
			profilePictureInputRef.current.click();
		}
	}, []);

	return {
		profilePictureInputRef,
		onProfilePictureClick,
	};
};
